import { useEffect, useRef } from 'react';
import { minutesToTime as toLabel, timeToMinutes as toMinutes } from '@motoschool/shared';
import { cn } from './ui';

interface TimeChipsProps {
  value: string;
  onChange: (value: string) => void;
  /** Границы рабочего дня школы. */
  from?: string;
  to?: string;
  stepMinutes?: number;
}

/**
 * Выбор времени нажатием вместо ввода в поле.
 * Нативный input[type=time] на телефоне требует попасть в мелкие цифры
 * и переключиться на цифровую клавиатуру, а времени начала занятий
 * всего пара десятков вариантов — их проще показать лентой.
 */
export function TimeChips({
  value,
  onChange,
  from = '06:00',
  to = '22:00',
  stepMinutes = 30,
}: TimeChipsProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const start = toMinutes(from);
  const end = toMinutes(to);

  const options: number[] = [];
  for (let minutes = start; minutes <= end; minutes += stepMinutes) {
    options.push(minutes);
  }

  // Выбранное время должно быть видно сразу, без ручной прокрутки ленты
  useEffect(() => {
    const container = containerRef.current;
    const active = activeRef.current;
    if (!container || !active) return;

    container.scrollTo({
      left: active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2,
      behavior: 'smooth',
    });
  }, [value]);

  return (
    <div
      ref={containerRef}
      className="snap-row no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 py-1"
      role="radiogroup"
      aria-label="Время"
    >
      {options.map((minutes) => {
        const label = toLabel(minutes);
        const isActive = label === value;

        return (
          <button
            key={label}
            ref={isActive ? activeRef : undefined}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(label)}
            className={cn(
              'snap-item min-h-11 shrink-0 rounded-xl border px-3 text-sm font-medium tabular-nums transition-colors',
              isActive
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-line bg-surface text-ink hover:bg-slate-50',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
